import { useEffect, useRef, useState } from "react";
import {
	Link,
	useCanGoBack,
	useRouter,
	type ErrorComponentProps,
} from "@tanstack/react-router";

import { Button, buttonVariants } from "@workspace/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";

import { resetClient } from "../lib/reset-client";
import { cn } from "../lib/utils";

type RouteError = Error & { digest?: string };

type ReportState =
	| { status: "idle" }
	| { status: "sending" }
	| { status: "sent"; code: string }
	| { status: "failed"; message: string };

export function ErrorComponent({ error: thrown, reset }: ErrorComponentProps) {
	// Router 1.170 types the thrown value as `unknown`, which it always was at runtime. This keeps
	// the component's existing reading of it (HA-735); it does not narrow or wrap non-Errors.
	const error = thrown as RouteError;
	console.error(error);

	let authError = false;
	let forbidden = false;

	if (error.name === "HttpRequestError") {
		const httpError = error as { response?: Response };
		// 401 means the session is gone (offer login); 403 means authenticated but
		// unauthorised, so the session stays and we render an access-denied state.
		authError = httpError.response?.status === 401;
		forbidden = httpError.response?.status === 403;
	}

	const isDevelopment = import.meta.env.DEV || import.meta.env.VITE_E2E;

	const router = useRouter();
	const canGoBack = useCanGoBack();
	const [report, setReport] = useState<ReportState>({ status: "idle" });
	const [resetPhase, setResetPhase] = useState<
		"idle" | "confirming" | "running"
	>("idle");
	const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(
		() => () => {
			if (confirmTimer.current) clearTimeout(confirmTimer.current);
		},
		[],
	);

	const handleResetClick = () => {
		if (resetPhase === "confirming") {
			if (confirmTimer.current) {
				clearTimeout(confirmTimer.current);
				confirmTimer.current = null;
			}
			setResetPhase("running");
			void resetClient();
			return;
		}
		if (resetPhase !== "idle") return;
		setResetPhase("confirming");
		confirmTimer.current = setTimeout(() => {
			confirmTimer.current = null;
			setResetPhase((p) => (p === "confirming" ? "idle" : p));
		}, 5000);
	};

	const resetLabel =
		resetPhase === "running"
			? "Resetting…"
			: resetPhase === "confirming"
				? "Are you sure?"
				: "Reset";

	if (forbidden) {
		return (
			<div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-950 p-4">
				<Card className="w-full max-w-md text-center">
					<CardHeader>
						<CardTitle className="text-2xl font-bold">Access denied</CardTitle>
						<CardDescription className="text-gray-600 dark:text-gray-400">
							You don&apos;t have access to this feature.
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Link
							to="/"
							className={cn(
								"w-full",
								buttonVariants({ className: "cursor-pointer" }),
							)}
						>
							Go to Home
						</Link>
					</CardContent>
				</Card>
			</div>
		);
	}

	const handleReport = async () => {
		setReport({ status: "sending" });
		try {
			const code = await reportError(error);
			setReport({ status: "sent", code });
		} catch (err) {
			setReport({
				status: "failed",
				message: err instanceof Error ? err.message : String(err),
			});
		}
	};

	return (
		<div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-950 p-4">
			<Card className="w-full max-w-md text-center">
				<CardHeader>
					<CardTitle className="text-2xl font-bold text-red-600 dark:text-red-400">
						Oops! Something went wrong.
					</CardTitle>
					<CardDescription className="text-gray-600 dark:text-gray-400">
						We&apos;re sorry, but an unexpected error occurred.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					{isDevelopment && (
						<div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-md text-left text-sm font-mono overflow-auto max-h-60">
							<p className="font-semibold text-gray-800 dark:text-gray-200">
								Error Details (Development Mode):
							</p>
							<pre className="whitespace-pre-wrap wrap-break-word text-gray-700 dark:text-gray-300">
								<code className="block">Message: {error.message}</code>
								{error.stack && (
									<code className="block mt-2">Stack: {error.stack}</code>
								)}
								{error.digest && (
									<code className="block mt-2">Digest: {error.digest}</code>
								)}
							</pre>
						</div>
					)}
					{authError && (
						<div>
							<p className="text-red-600 dark:text-red-400">
								You may need to log in again.
							</p>
							<Link
								to="/logout"
								className={cn(
									"w-full",
									buttonVariants({
										className: "cursor-pointer",
									}),
								)}
							>
								Login
							</Link>
						</div>
					)}
					{!authError && (
						<div className="space-y-2">
							<Button onClick={reset} size="lg" className="w-full">
								Try again
							</Button>

							<Button
								onClick={handleResetClick}
								variant={
									resetPhase === "confirming" ? "outline" : "destructive"
								}
								size="sm"
								disabled={resetPhase === "running"}
							>
								{resetLabel}
							</Button>
							<p className="text-xs text-muted-foreground">
								Clears this browser&apos;s cached app data and reloads.
								You&apos;ll stay signed in.
							</p>
						</div>
					)}

					<div className="space-y-2 border-t pt-4">
						<Button
							onClick={() => void handleReport()}
							variant="outline"
							size="sm"
							className="w-full"
							disabled={report.status === "sending" || report.status === "sent"}
						>
							{report.status === "sending"
								? "Sending…"
								: report.status === "sent"
									? "Reported"
									: "Report problem"}
						</Button>
						{report.status === "sent" ? (
							<p className="text-xs text-muted-foreground">
								Reported. Share this code with support:{" "}
								<span className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm font-semibold text-foreground">
									{report.code}
								</span>
							</p>
						) : null}
						{report.status === "failed" ? (
							<p className="text-xs text-destructive">
								Couldn&apos;t send report: {report.message}
							</p>
						) : null}
					</div>

					<p>
						{canGoBack ? (
							<Button
								onClick={() => router.history.back()}
								variant="link"
								className="cursor-pointer"
							>
								Go back
							</Button>
						) : (
							<Link
								to="/"
								className={buttonVariants({
									variant: "link",
									className: "cursor-pointer",
								})}
							>
								Go to Home
							</Link>
						)}
					</p>
				</CardContent>
			</Card>
		</div>
	);
}

async function reportError(error: RouteError): Promise<string> {
	const apiBase = import.meta.env.VITE_API_URL;
	if (!apiBase) throw new Error("VITE_API_URL not configured");

	const url = new URL("/diagnostics/report", apiBase);

	const notes = [
		`URL: ${typeof window !== "undefined" ? window.location.href : "unknown"}`,
		`Error: ${error.name}: ${error.message}`,
		error.digest ? `Digest: ${error.digest}` : null,
		error.stack ? `Stack:\n${error.stack}` : null,
	]
		.filter(Boolean)
		.join("\n\n");

	const connection = readConnection();
	const body = {
		tenantHint: null,
		email: null,
		notes,
		environment: {
			userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
			language: typeof navigator !== "undefined" ? navigator.language : null,
			timezone:
				typeof Intl !== "undefined"
					? Intl.DateTimeFormat().resolvedOptions().timeZone
					: null,
			screenWidth: typeof window !== "undefined" ? window.screen.width : null,
			screenHeight: typeof window !== "undefined" ? window.screen.height : null,
			devicePixelRatio:
				typeof window !== "undefined" ? window.devicePixelRatio : null,
			connectionEffectiveType: connection?.effectiveType,
			connectionRtt: connection?.rtt,
			connectionDownlink: connection?.downlink,
			connectionSaveData: connection?.saveData,
		},
		timings: { pingMs: [] },
		edge: null,
	};

	const res = await fetch(url.toString(), {
		method: "POST",
		headers: {
			"content-type": "application/json",
			tenant: readTenant() || "",
		},
		body: JSON.stringify(body),
	});
	if (!res.ok) throw new Error(`HTTP ${res.status}`);
	const json = (await res.json()) as { id: string };
	return json.id;
}

type ConnectionInfo = {
	effectiveType?: string;
	rtt?: number;
	downlink?: number;
	saveData?: boolean;
};

const TENANT_KEY = "app.tenant.current";

function readTenant(): string | null {
	if (typeof document !== "undefined") {
		const match = document.cookie
			.split(";")
			.map((c) => c.trim())
			.find((c) => c.startsWith(`${TENANT_KEY}=`));
		if (match) {
			const value = decodeURIComponent(match.slice(TENANT_KEY.length + 1));
			if (value) return value;
		}
	}
	if (typeof localStorage !== "undefined") {
		try {
			const value = localStorage.getItem(TENANT_KEY);
			if (value) return value;
		} catch {
			/* ignore */
		}
	}
	return null;
}

function readConnection(): ConnectionInfo | undefined {
	if (typeof navigator === "undefined") return undefined;
	const c = (navigator as unknown as { connection?: ConnectionInfo })
		.connection;
	if (!c) return undefined;
	return {
		effectiveType: c.effectiveType,
		rtt: c.rtt,
		downlink: c.downlink,
		saveData: c.saveData,
	};
}
